import dayjs, { Dayjs } from 'dayjs';

/**
 * The backend holds no analytics data older than this many years, so no
 * analytics date may be selected further back than `today - N years`.
 */
export const ANALYTICS_MAX_HISTORY_YEARS = 2;

/** The oldest selectable analytics date: start of day, `N` years before today. */
export function getAnalyticsMinSelectableDate(): Dayjs {
  return dayjs().subtract(ANALYTICS_MAX_HISTORY_YEARS, 'year').startOf('day');
}

/** Pulls a date up to the history floor if it is older than it. */
export function clampToAnalyticsMinDate(date: Dayjs): Dayjs {
  const min = getAnalyticsMinSelectableDate();
  return date.isBefore(min, 'day') ? min : date;
}

export interface AnalyticsDateRange {
  startDate: Dayjs;
  endDate: Dayjs;
}

/**
 * Fits a derived range above the history floor without changing its length: a
 * range starting below the floor is shifted forward whole, never clipped.
 *
 * Returns `null` when no equal-length range fits before `mainStartDate` — the
 * shifted window would overlap the very period it compares against.
 */
export function fitRangeAboveAnalyticsMinDate(
  range: AnalyticsDateRange,
  mainStartDate: Dayjs,
): AnalyticsDateRange | null {
  const offsetToEnd = range.endDate.diff(range.startDate, 'day');
  const startDate = clampToAnalyticsMinDate(range.startDate);
  const endDate = startDate.add(offsetToEnd, 'day');

  if (!endDate.isBefore(mainStartDate, 'day')) {
    return null;
  }

  return { startDate, endDate };
}
